/**
 * RTMPose Adapter — implements the canonical InputAdapter contract for
 * MMPose's RTMPose family via ONNX Runtime Web.
 *
 * Top-down design: a person bounding box must be supplied per
 * pollFrame() invocation. The host couples this adapter with the
 * existing YOLOv8n detector (see RTMDetCoupling.ts) or any other
 * source of bboxes; passing no bbox triggers a single full-frame
 * pass which is robust for single-subject footage.
 *
 * docs/architecture-spec.md §5.2 (InputAdapter), §11 (Conformance)
 * docs/migration-plan.md Phase 11
 */

import * as ort from 'onnxruntime-web';

import {
  CANONICAL_SCHEMA_VERSION,
  type AdapterCapabilities,
  type AdapterFatalError,
  type AdapterHealth,
  type AdapterPlatform,
  type CanonicalFrame,
  type InputAdapter,
  type QualityProfile,
  type StreamHeader,
  type Subscription,
} from '../../canonical';
import {
  CANONICAL_JOINT_KEYS,
  FACE_JOINT_KEYS,
  type CanonicalJointKey,
} from '../../canonical/JointKeys';
import { loadRTMPoseSession, type RtmPoseSession, type RtmPoseTier } from './loader';
import { normalizeRTMPoseBody, type RTMPoseRawKeypoint } from './SkeletonNormalizer';

const ADAPTER_VERSION = '0.1.0';

export interface RTMPoseAdapterOptions {
  /** `t` (mobile-friendly), `m` (default), `l` (PC accuracy). */
  tier: RtmPoseTier;
  /** Stable hash, never a raw device identifier. Host responsibility. */
  deviceIdHash: string;
  sensorIds?: string[];
  /** Optional model URL override (for self-hosting). */
  modelUrl?: string;
}

export interface RTMPoseBoundingBox {
  /** Normalised x,y,w,h in [0,1] source-image space. */
  x: number;
  y: number;
  w: number;
  h: number;
  score?: number;
}

const DEFAULT_QUALITY: QualityProfile = {
  modelPreset: 'auto',
  inputResolutionPx: null,
  targetFps: null,
  pii: {
    faceLandmarks: 'include',
    faceSegmentation: 'include',
    depthMap: 'include',
    arWorldAnchors: 'include',
  },
};

const FULL_CAPABILITIES: AdapterCapabilities = {
  pose: true,
  // RTMPose-COCO17 is purely 2D — 3D world coords come from a
  // downstream PoseLifter (Phase 12) when present.
  pose3DWorld: false,
  pose3DLocal: false,
  depth: false,
  segmentation: false,
  cameraIntrinsics: false,
  cameraExtrinsics: false,
  arAnchors: false,
  realtime: true,
  // Top-down inference can be invoked per detected person; the host
  // supplies bboxes from the existing YOLOv8n detector.
  multiPerson: true,
  persistentTracking: false,
  pii: {
    faceLandmarks: true,
    faceSegmentation: false,
    depthMap: false,
    deviceFingerprint: true,
    arWorldAnchors: false,
  },
};

export class RTMPoseAdapter implements InputAdapter {
  readonly id: string;
  readonly version = ADAPTER_VERSION;
  readonly capabilities = FULL_CAPABILITIES;
  readonly supportedSkeletons = ['rtmpose-coco17'] as const;
  readonly supportedPlatforms: readonly AdapterPlatform[] = ['web'];

  private session: RtmPoseSession | null = null;
  private streamId: string;
  private startedAtMonotonicNs = 0;
  private nextFrameId = 0;
  private quality: QualityProfile = { ...DEFAULT_QUALITY };
  private health: AdapterHealth = createInitialHealth(this.capabilities);
  private healthSubs = new Set<(h: AdapterHealth) => void>();
  private fatalSubs = new Set<(e: AdapterFatalError) => void>();
  private frameSubs = new Set<(f: CanonicalFrame) => void>();
  /** Critical-window watermark; the source driver SHOULD avoid drops
   *  until this wallclock instant. RTMPose itself runs blocking inside
   *  pollFrame() so we record the request but don't actively gate. */
  private noDropUntilMs = 0;
  /** Returns true if the current wallclock falls inside an active
   *  no-drop window requested by a Core module. */
  isInsideCriticalPhase(): boolean {
    return nowWallMs() < this.noDropUntilMs;
  }
  /** Canvas reused across pollFrame() invocations to avoid GC pressure. */
  private cropCanvas: HTMLCanvasElement | null = null;

  constructor(private readonly opts: RTMPoseAdapterOptions) {
    this.id = `rtmpose-${opts.tier}`;
    this.streamId = `${this.id}-${cryptoRandomId()}`;
  }

  async initialize(): Promise<StreamHeader> {
    this.health = { ...this.health, state: 'initializing' };
    this.publishHealth();

    this.session = await loadRTMPoseSession({
      tier: this.opts.tier,
      modelUrl: this.opts.modelUrl,
    });
    this.cropCanvas = document.createElement('canvas');
    this.cropCanvas.width = this.session.inputWidth;
    this.cropCanvas.height = this.session.inputHeight;

    this.startedAtMonotonicNs = nowMonotonicNs();
    const header: StreamHeader = {
      schemaVersion: CANONICAL_SCHEMA_VERSION,
      streamId: this.streamId,
      startedAt: {
        monotonicEpochNs: this.startedAtMonotonicNs,
        wallclockMs: Date.now(),
      },
      adapter: {
        id: this.id,
        version: this.version,
        capabilities: this.capabilities,
        // SimCC peak score behaves like MediaPipe's `presence`
        // (probability the joint is in-frame and detected).
        confidenceModel: 'presence',
      },
      device: {
        id: this.opts.deviceIdHash,
        sensorIds: this.opts.sensorIds ?? [],
      },
    };
    return header;
  }

  async start(): Promise<void> {
    if (!this.session) throw new Error('RTMPoseAdapter: not initialised');
    this.health = { ...this.health, state: 'running' };
    this.publishHealth();
  }

  async stop(): Promise<void> {
    this.health = { ...this.health, state: 'stopped' };
    this.publishHealth();
  }

  async dispose(): Promise<void> {
    try {
      await this.session?.session.release?.();
    } catch {
      /* ignore */
    }
    this.session = null;
    this.cropCanvas = null;
    this.healthSubs.clear();
    this.fatalSubs.clear();
    this.frameSubs.clear();
  }

  /**
   * Pull-mode acquisition. The host passes the source surface and
   * (optionally) a list of person bounding boxes from an external
   * detector. When no bboxes are provided we run full-frame for the
   * single-subject case.
   */
  async pollFrame(
    source?: CanvasImageSource,
    timestampMs?: number,
    bboxes?: readonly RTMPoseBoundingBox[],
  ): Promise<CanonicalFrame | null> {
    if (!this.session || !source || typeof timestampMs !== 'number') return null;
    const dims = sourceDims(source);
    if (dims.width <= 0 || dims.height <= 0) return null;

    const boxes =
      bboxes && bboxes.length > 0
        ? bboxes
        : ([{ x: 0, y: 0, w: 1, h: 1 }] as const);

    const excludeFace = this.quality.pii.faceLandmarks === 'exclude';
    const bodies = [];
    for (let i = 0; i < boxes.length; i += 1) {
      const kps = await this.runOne(source, dims, boxes[i]);
      if (!kps) continue;
      bodies.push(
        normalizeRTMPoseBody(kps, {
          sourceWidthPx: dims.width,
          sourceHeightPx: dims.height,
          pixelAspect: 1,
          frameLocalId: i,
          skeletonId: 'rtmpose-coco17',
          excludeFaceLandmarks: excludeFace,
        }),
      );
    }

    const monotonicNs = wallMsToMonotonicNs(timestampMs, this.startedAtMonotonicNs);
    const frame: CanonicalFrame = {
      streamId: this.streamId,
      frameId: this.nextFrameId++,
      captureTime: {
        monotonicNs,
        wallclockMs: null,
        sourceTimeNs: null,
      },
      pose: { bodies, sampleTimeNs: monotonicNs },
      depth: null,
      camera: null,
      world: null,
      alignment: { method: 'strict-equal', maxSkewMs: 0 },
      precedingDrop: null,
    };

    for (const cb of this.frameSubs) cb(frame);
    return frame;
  }

  onFrame(cb: (frame: CanonicalFrame) => void): Subscription {
    this.frameSubs.add(cb);
    return makeSub(() => this.frameSubs.delete(cb));
  }

  reportHealth(): AdapterHealth {
    return this.health;
  }

  onHealthChange(cb: (h: AdapterHealth) => void): Subscription {
    this.healthSubs.add(cb);
    return makeSub(() => this.healthSubs.delete(cb));
  }

  onFatalError(cb: (e: AdapterFatalError) => void): Subscription {
    this.fatalSubs.add(cb);
    return makeSub(() => this.fatalSubs.delete(cb));
  }

  setQualityProfile(profile: QualityProfile): void {
    this.quality = { ...profile };
    const effective = this.computeEffectiveCapabilities();
    this.health = {
      ...this.health,
      effectiveCapabilities: effective.caps,
      effectiveJointKeys: effective.joints,
    };
    this.publishHealth();
  }

  requestNoDrop(windowMs: number): void {
    this.noDropUntilMs = nowWallMs() + Math.max(0, windowMs);
  }

  // ------------------------------------------------------------------
  // internals
  // ------------------------------------------------------------------

  /**
   * Single-bbox forward pass. Crops the source image to the bbox,
   * resizes onto the model's input resolution, runs the SimCC head
   * and decodes peak indices back into normalised image-space coords.
   */
  private async runOne(
    source: CanvasImageSource,
    dims: { width: number; height: number },
    bbox: RTMPoseBoundingBox,
  ): Promise<RTMPoseRawKeypoint[] | null> {
    if (!this.session || !this.cropCanvas) return null;
    const ctx = this.cropCanvas.getContext('2d');
    if (!ctx) return null;

    const { inputWidth, inputHeight, session, inputName, outputNames } = this.session;
    const sx = Math.max(0, bbox.x * dims.width);
    const sy = Math.max(0, bbox.y * dims.height);
    const sw = Math.max(1, Math.min(dims.width - sx, bbox.w * dims.width));
    const sh = Math.max(1, Math.min(dims.height - sy, bbox.h * dims.height));

    ctx.clearRect(0, 0, inputWidth, inputHeight);
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, inputWidth, inputHeight);
    const image = ctx.getImageData(0, 0, inputWidth, inputHeight);
    const tensor = imageToCHW(image, inputWidth, inputHeight);

    let result: ort.InferenceSession.OnnxValueMapType;
    try {
      result = await session.run({ [inputName]: tensor });
    } catch (err) {
      this.publishFatal({
        code: 'rtmpose-runtime',
        message: err instanceof Error ? err.message : String(err),
        recoverable: false,
      });
      return null;
    }

    // SimCC outputs: simcc_x [1, K, W*split], simcc_y [1, K, H*split].
    // K = 17 for COCO. Decode argmax along each axis → (px, py) → normalise
    // back to bbox-relative coords, then compose with the bbox to get
    // image-relative normalised coords.
    const simccX = result[outputNames[0]]?.data as Float32Array | undefined;
    const simccY = result[outputNames[1]]?.data as Float32Array | undefined;
    if (!simccX || !simccY) return null;

    const numKp = 17;
    const splitX = simccX.length / numKp;
    const splitY = simccY.length / numKp;
    const out: RTMPoseRawKeypoint[] = [];
    for (let k = 0; k < numKp; k += 1) {
      const offX = k * splitX;
      const offY = k * splitY;
      let bestX = 0;
      let bestXScore = -Infinity;
      for (let i = 0; i < splitX; i += 1) {
        const v = simccX[offX + i];
        if (v > bestXScore) {
          bestXScore = v;
          bestX = i;
        }
      }
      let bestY = 0;
      let bestYScore = -Infinity;
      for (let j = 0; j < splitY; j += 1) {
        const v = simccY[offY + j];
        if (v > bestYScore) {
          bestYScore = v;
          bestY = j;
        }
      }
      // Recover bbox-relative coords (0..1) then place back into the
      // full image. SimCC bins map linearly to the network input;
      // network input maps linearly to bbox coords.
      const bboxLocalX = bestX / Math.max(1, splitX - 1);
      const bboxLocalY = bestY / Math.max(1, splitY - 1);
      const imgX = bbox.x + bboxLocalX * bbox.w;
      const imgY = bbox.y + bboxLocalY * bbox.h;
      const score = sigmoid01(0.5 * (bestXScore + bestYScore));
      out.push({ x: imgX, y: imgY, score });
    }
    return out;
  }

  private computeEffectiveCapabilities(): {
    caps: AdapterCapabilities;
    joints: CanonicalJointKey[];
  } {
    const caps: AdapterCapabilities = {
      ...this.capabilities,
      pii: { ...this.capabilities.pii },
    };
    if (this.quality.pii.faceLandmarks === 'exclude') {
      caps.pii.faceLandmarks = false;
    }
    if (this.quality.pii.faceSegmentation === 'exclude') {
      caps.pii.faceSegmentation = false;
    }
    if (this.quality.pii.depthMap === 'exclude') {
      caps.pii.depthMap = false;
    }
    if (this.quality.pii.arWorldAnchors === 'exclude') {
      caps.pii.arWorldAnchors = false;
    }

    const joints: CanonicalJointKey[] =
      this.quality.pii.faceLandmarks === 'exclude'
        ? CANONICAL_JOINT_KEYS.filter((k) => !(FACE_JOINT_KEYS as readonly string[]).includes(k))
        : [...CANONICAL_JOINT_KEYS];
    return { caps, joints };
  }

  private publishHealth(): void {
    for (const cb of this.healthSubs) cb(this.health);
  }

  private publishFatal(e: AdapterFatalError): void {
    for (const cb of this.fatalSubs) cb(e);
  }
}

// ---- helpers --------------------------------------------------------

function createInitialHealth(caps: AdapterCapabilities): AdapterHealth {
  return {
    state: 'idle',
    fps: 0,
    droppedFrames: 0,
    lastFrameAgeMs: 0,
    lastWarning: null,
    effectiveCapabilities: { ...caps, pii: { ...caps.pii } },
    effectiveJointKeys: [...CANONICAL_JOINT_KEYS],
  };
}

function makeSub(unsubscribe: () => void): Subscription {
  let active = true;
  return {
    unsubscribe(): void {
      if (!active) return;
      active = false;
      unsubscribe();
    },
  };
}

function sourceDims(source: CanvasImageSource): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) {
    return { width: source.videoWidth, height: source.videoHeight };
  }
  if (source instanceof HTMLCanvasElement) {
    return { width: source.width, height: source.height };
  }
  if ('width' in source && 'height' in source) {
    return { width: Number(source.width), height: Number(source.height) };
  }
  return { width: 0, height: 0 };
}

function imageToCHW(image: ImageData, width: number, height: number): ort.Tensor {
  const { data } = image;
  const plane = width * height;
  const rgb = new Float32Array(3 * plane);
  // RTMPose uses ImageNet normalisation: mean=[0.485,0.456,0.406] std=[0.229,0.224,0.225].
  const mean = [0.485, 0.456, 0.406];
  const std = [0.229, 0.224, 0.225];
  for (let i = 0; i < plane; i += 1) {
    const base = i * 4;
    rgb[i] = (data[base] / 255 - mean[0]) / std[0];
    rgb[i + plane] = (data[base + 1] / 255 - mean[1]) / std[1];
    rgb[i + 2 * plane] = (data[base + 2] / 255 - mean[2]) / std[2];
  }
  return new ort.Tensor('float32', rgb, [1, 3, height, width]);
}

function sigmoid01(v: number): number {
  // SimCC peaks aren't bounded; squash with a logistic so downstream
  // code can treat the score uniformly with MediaPipe visibility.
  const s = 1 / (1 + Math.exp(-v));
  return s < 0 ? 0 : s > 1 ? 1 : s;
}

function nowMonotonicNs(): number {
  return Math.round(performance.now() * 1e6);
}

function nowWallMs(): number {
  return performance.now();
}

function wallMsToMonotonicNs(wallMs: number, startedAtNs: number): number {
  return Math.max(0, Math.round(wallMs * 1e6) - startedAtNs);
}

function cryptoRandomId(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}
